import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiRotateCcw, FiAlertCircle, FiLock, FiSearch } from 'react-icons/fi';
import { promptService } from '../../services/apiService';
import { useAuthStore } from '../../utils/store';
import { useT } from '../../utils/i18n';
import { formatISTDateTime } from '../../utils/dateUtils';

export const PromptEditor = () => {
  const t = useT();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [searchParams] = useSearchParams();

  const [prompts, setPrompts] = useState([]); // [{ contentType, label, notes, hasOverride, ... }]
  const [selectedType, setSelectedType] = useState('');
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState({ userPromptTemplate: '', systemPrompt: '', notes: '' });
  const [filterText, setFilterText] = useState('');

  const [loading, setLoading] = useState(true);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState('');

  // ── Load the full prompt list (admins only) ─────────────────────────────────
  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const data = await promptService.listPrompts();
        // GET /api/prompts returns an object keyed by contentType, e.g.
        // { anchorVisual: { label, systemPrompt, ... }, ... } — normalise to an array.
        const list = Object.entries(data || {}).map(([contentType, entry]) => ({
          contentType,
          ...entry,
        }));
        setPrompts(list);

        const typeParam = searchParams.get('type');
        const preselect = (typeParam && list.some((p) => p.contentType === typeParam))
          ? typeParam
          : list[0]?.contentType;
        if (preselect) setSelectedType(preselect);
      } catch (e) {
        console.error('[PromptEditor] Failed to load prompt list:', e);
        setError(t('settings.failedLoadPrompts'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAdmin]);

  // ── Load the selected prompt's full entry into the editor ───────────────────
  useEffect(() => {
    if (!isAdmin || !selectedType) return;
    let cancelled = false;
    const fetchOne = async () => {
      setError('');
      setLoadingPrompt(true);
      try {
        const data = await promptService.getPrompt(selectedType);
        if (cancelled) return;
        setSelected(data);
        setFormData({
          userPromptTemplate: data.userPromptTemplate || '',
          systemPrompt: data.systemPrompt || '',
          notes: data.notes || '',
        });
      } catch (e) {
        if (cancelled) return;
        console.error('[PromptEditor] Failed to load prompt:', selectedType, e);
        setError(t('settings.failedLoadPrompt'));
      } finally {
        if (!cancelled) setLoadingPrompt(false);
      }
    };
    fetchOne();
    return () => { cancelled = true; };
  }, [selectedType, isAdmin]);

  const refreshListEntry = useCallback((contentType, patch) => {
    setPrompts((list) => list.map((p) => (p.contentType === contentType ? { ...p, ...patch } : p)));
  }, []);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!formData.userPromptTemplate.trim()) {
      setError(t('settings.userPromptRequired'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const updated = await promptService.updatePrompt(selectedType, {
        userPromptTemplate: formData.userPromptTemplate,
        systemPrompt: formData.systemPrompt,
        notes: formData.notes,
      });
      console.log('[PromptEditor] Save response:', updated);
      setSelected(updated);
      refreshListEntry(selectedType, {
        label: updated.label,
        notes: updated.notes,
        hasOverride: updated.hasOverride,
        systemPrompt: updated.systemPrompt,
        userPromptTemplate: updated.userPromptTemplate,
      });
      toast.success(t('settings.saveSuccess'));
    } catch (e) {
      console.error('[PromptEditor] Save failed:', e.response?.data || e.message);
      const detail = e.response?.data?.errors?.join(', ') || e.response?.data?.message || e.message;
      setError(detail || t('settings.failedSavePrompt'));
      toast.error(detail || t('settings.failedSavePrompt'));
    } finally {
      setSaving(false);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────
  const handleReset = async () => {
    if (!window.confirm(t('settings.resetConfirm'))) return;
    setResetting(true);
    setError('');
    try {
      const defaultData = await promptService.deleteOverride(selectedType);
      console.log('[PromptEditor] Reset response:', defaultData);
      setSelected(defaultData);
      setFormData({
        userPromptTemplate: defaultData?.userPromptTemplate || '',
        systemPrompt: defaultData?.systemPrompt || '',
        notes: defaultData?.notes || '',
      });
      refreshListEntry(selectedType, {
        label: defaultData?.label,
        notes: defaultData?.notes,
        hasOverride: false,
        systemPrompt: defaultData?.systemPrompt,
        userPromptTemplate: defaultData?.userPromptTemplate,
      });
      toast.success(t('settings.resetSuccess'));
    } catch (e) {
      console.error('[PromptEditor] Reset failed:', e.response?.data || e.message);
      const detail = e.response?.data?.message || e.message;
      setError(detail || t('settings.failedResetPrompt'));
      toast.error(detail || t('settings.failedResetPrompt'));
    } finally {
      setResetting(false);
    }
  };

  // ── Access gate — defense-in-depth alongside the router-level adminOnly guard ─
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center text-center h-64 max-w-md mx-auto gap-3">
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
          <FiLock size={20} className="text-gray-400" />
        </div>
        <h1 className="text-lg font-semibold text-gray-900">
          {t('settings.promptEditorAccessRestricted')}
        </h1>
        <p className="text-sm text-gray-500">
          {t('settings.promptEditorAdminOnly')}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400">
        {t('settings.loadingPrompts')}
      </div>
    );
  }

  const currentPrompt = prompts.find((p) => p.contentType === selectedType);
  const filteredPrompts = filterText.trim()
    ? prompts.filter((p) =>
        (p.label || p.contentType).toLowerCase().includes(filterText.trim().toLowerCase())
      )
    : prompts;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.promptEditor')}</h1>
        <p className="text-sm text-gray-500 mt-1">
          {t('settings.promptEditorSubtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">

        {/* Left: list of all prompt types */}
        <div className="card p-0 overflow-hidden flex flex-col max-h-[70vh]">
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <FiSearch size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder={t('settings.contentType')}
                className="input w-full text-sm pl-7"
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {filteredPrompts.map((p) => (
              <button
                key={p.contentType}
                onClick={() => setSelectedType(p.contentType)}
                className={`w-full text-left px-3 py-2.5 text-sm border-b border-gray-50 transition flex items-center justify-between gap-2 ${
                  selectedType === p.contentType
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="truncate">{p.label || p.contentType}</span>
                {p.hasOverride && (
                  <span className="text-primary-500 flex-shrink-0" title={t('settings.customOverrideActive')}>✦</span>
                )}
              </button>
            ))}
            {filteredPrompts.length === 0 && (
              <p className="px-3 py-4 text-xs text-gray-400">{t('settings.failedLoadPrompts')}</p>
            )}
          </div>
        </div>

        {/* Right: editor panel */}
        <div className="space-y-4 min-w-0">
          {!selected ? (
            <div className="card p-6 text-sm text-gray-400 text-center">
              {loadingPrompt ? t('settings.loadingPrompts') : t('settings.selectPromptType')}
            </div>
          ) : (
            <>
              {/* Label heading + override / last-modified info */}
              <div className="card p-4 space-y-1">
                <h2 className="text-lg font-semibold text-gray-900">{selected.label || selectedType}</h2>
                {currentPrompt?.hasOverride && (
                  <p className="text-xs text-primary-600">{t('settings.customOverrideActive')}</p>
                )}
                {selected.hasOverride && selected.overrideUpdatedAt && (
                  <p className="text-xs text-gray-400">
                    {t('settings.lastModifiedBy')}: {selected.updatedBy?.name || selected.updatedBy?.email || '—'}{' '}
                    {t('settings.on')}{' '}
                    {formatISTDateTime(selected.overrideUpdatedAt)}
                  </p>
                )}
              </div>

              {/* System Prompt */}
              <div className="card p-4 space-y-2">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  {t('settings.systemPromptFullHint')}
                </label>
                <textarea
                  rows={6}
                  value={formData.systemPrompt}
                  onChange={(e) => setFormData((f) => ({ ...f, systemPrompt: e.target.value }))}
                  className="input w-full font-mono text-sm resize-y"
                  placeholder={t('settings.systemPromptPlaceholder')}
                />
              </div>

              {/* User Prompt Template */}
              <div className="card p-4 space-y-2">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  {t('settings.userPromptFullHint')} <span className="text-danger-500">*</span>
                </label>
                <textarea
                  rows={10}
                  value={formData.userPromptTemplate}
                  onChange={(e) => setFormData((f) => ({ ...f, userPromptTemplate: e.target.value }))}
                  className="input w-full font-mono text-sm resize-y"
                  placeholder={t('settings.userPromptPlaceholder')}
                />
              </div>

              {/* Editorial Notes */}
              <div className="card p-4 space-y-2">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  {t('settings.editorialNotes')} <span className="text-gray-400 font-normal normal-case">{t('settings.optional')}</span>
                </label>
                <textarea
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData((f) => ({ ...f, notes: e.target.value }))}
                  className="input w-full text-sm resize-y"
                  placeholder={t('settings.notesPlaceholder')}
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 text-sm text-danger-600 bg-danger-50 border border-danger-200 rounded-lg px-3 py-2">
                  <FiAlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Action bar */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-primary flex items-center gap-1.5"
                >
                  <FiSave size={14} />
                  {saving ? t('settings.saving') : t('settings.saveChanges')}
                </button>
                <button
                  onClick={handleReset}
                  disabled={resetting}
                  className="btn btn-ghost flex items-center gap-1.5 text-gray-600"
                >
                  <FiRotateCcw size={14} />
                  {resetting ? t('settings.resetting') : t('settings.resetToDefault')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
